import React, { useEffect, useState } from 'react'
import { Box, Flex, SwitchWithLabels } from 'front-end/src/components'
import {
  TargetEnrolmentStatusEnum,
  TemporalDirection
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  feEnrolmentTimingsFilterEnum,
  getEnrolmentTimingOptionsMap,
  transformToSelectOptions
} from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/CampaignAudienceHelpers'
import { Trans } from '@lingui/macro'
import Select from '../../../../../components/common/select/Select'
import { css } from '@emotion/react'
import { useEditCampaignFilterContext } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/enrolment-modal/EditCampaignFilterContext'
import { EditCampaignFilterRelativeDate } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/EditCampaignFilterRelativeDate'
import { EditCampaignFilterStaticDate } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/EditCampaignFilterStaticDate'
import { EditCampaignFilterDateRange } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/EditCampaignFilterDateRange'

export const EditCampaignFilterDate = ({ setSelectedEnrolmentStateType }) => {
  const { editedFilter, setEditedFilter } = useEditCampaignFilterContext()

  const setTemporalComparator = (temporalComparator) => {
    setEditedFilter({
      ...editedFilter,
      temporalComparator
    })
  }

  const selectedStatus = editedFilter.enrolmentFilterSetup?.enrolmentFilterType
  const isExpiresStatus =
    selectedStatus === TargetEnrolmentStatusEnum.expires ||
    selectedStatus === TargetEnrolmentStatusEnum.expired_or_expires

  let defaultTimingType = feEnrolmentTimingsFilterEnum.NONE

  if (editedFilter.temporalComparator?.relative) {
    defaultTimingType =
      editedFilter.temporalComparator?.relative.direction ===
      TemporalDirection.BEFORE
        ? feEnrolmentTimingsFilterEnum.MORE_THAN
        : feEnrolmentTimingsFilterEnum.LESS_THAN
  } else if (editedFilter.temporalComparator?.static) {
    defaultTimingType =
      editedFilter.temporalComparator?.static.direction ===
      TemporalDirection.BEFORE
        ? feEnrolmentTimingsFilterEnum.BEFORE_DATE
        : feEnrolmentTimingsFilterEnum.AFTER_DATE
  } else if (editedFilter.temporalComparator?.range) {
    defaultTimingType = feEnrolmentTimingsFilterEnum.DATE_RANGE
  }

  const [includeExpired, setIncludeExpired] = useState(
    selectedStatus !== TargetEnrolmentStatusEnum.expires
  )

  const [selectedEnrolmentTimingType, setSelectedEnrolmentTimingType] =
    useState<feEnrolmentTimingsFilterEnum>(defaultTimingType)

  useEffect(() => {
    if (isExpiresStatus) {
      setSelectedEnrolmentStateType(
        includeExpired
          ? TargetEnrolmentStatusEnum.expired_or_expires
          : TargetEnrolmentStatusEnum.expires
      )
      if (selectedEnrolmentTimingType === feEnrolmentTimingsFilterEnum.NONE) {
        setSelectedEnrolmentTimingType(feEnrolmentTimingsFilterEnum.MORE_THAN)
      }
    }
  }, [includeExpired, isExpiresStatus])

  const enrolmentTimingOptions = transformToSelectOptions(
    getEnrolmentTimingOptionsMap(isExpiresStatus)
  )

  const relativeTimingTypes = [
    feEnrolmentTimingsFilterEnum.MORE_THAN,
    feEnrolmentTimingsFilterEnum.LESS_THAN
  ]

  const staticTimingTypes = [
    feEnrolmentTimingsFilterEnum.BEFORE_DATE,
    feEnrolmentTimingsFilterEnum.AFTER_DATE
  ]

  const renderDateLabel = () => {
    const isLastViewedStatus =
      selectedStatus === TargetEnrolmentStatusEnum.last_viewed_at

    if (isLastViewedStatus) {
      return null
    }

    if (relativeTimingTypes.includes(selectedEnrolmentTimingType)) {
      if (
        isExpiresStatus ||
        selectedEnrolmentTimingType === feEnrolmentTimingsFilterEnum.LESS_THAN
      ) {
        return <Trans>in</Trans>
      }
      return <Trans>for</Trans>
    }
    return null
  }

  return (
    <>
      <Flex mb="8px">
        <Flex
          mr="16px"
          align="center"
          justify="flex-end"
          w="164px"
          textAlign="right"
          lineHeight="20px"
        >
          {renderDateLabel()}
        </Flex>

        <Box mr="16px">
          <Select
            options={enrolmentTimingOptions}
            value={enrolmentTimingOptions.find(({ value }) => {
              return value === selectedEnrolmentTimingType
            })}
            css={css`
              .Select__control {
                min-width: 188px;
              }
            `}
            onChange={(option) => {
              setSelectedEnrolmentTimingType(option?.value)

              if (option?.value === feEnrolmentTimingsFilterEnum.NONE) {
                setTemporalComparator(undefined)
              }
            }}
          />
        </Box>

        <Flex>
          {relativeTimingTypes.includes(selectedEnrolmentTimingType) && (
            <EditCampaignFilterRelativeDate
              setTemporalComparator={setTemporalComparator}
              selectedEnrolmentTimingType={selectedEnrolmentTimingType}
              showAgoLabel={
                selectedStatus === TargetEnrolmentStatusEnum.last_viewed_at
              }
            />
          )}
          {staticTimingTypes.includes(selectedEnrolmentTimingType) && (
            <EditCampaignFilterStaticDate
              setTemporalComparator={setTemporalComparator}
              selectedEnrolmentTimingType={selectedEnrolmentTimingType}
            />
          )}
        </Flex>
      </Flex>

      {selectedEnrolmentTimingType ===
        feEnrolmentTimingsFilterEnum.DATE_RANGE && (
        <EditCampaignFilterDateRange
          setTemporalComparator={setTemporalComparator}
        />
      )}

      {isExpiresStatus && (
        <Flex pl="180px">
          <Box mr="16px">
            <Trans>including already expired</Trans>
          </Box>

          <SwitchWithLabels
            onChange={() => {
              setIncludeExpired(!includeExpired)
            }}
            isChecked={includeExpired}
          />
        </Flex>
      )}
    </>
  )
}
